<template>
  <div id="app">
    <div v-if="!(recipesLoaded && newsLoaded)" id="loading-screen">
      <h1>Loading...</h1>
    </div>
    <div v-if="handholdingText" id="handholding">
      <div id="handholding-wrapper">
        <i class="fas fa-arrow-up" />
        <span>{{ handholdingText }}</span>
      </div>
    </div>
    <div v-if="showPinToTaskbar" id="pin-to-taskbar">
      <div class="content">
        <span>Right click on the <img src="/img/icons/android-icon-144x144.png"> icon below</span>
        <span>then click "<b>Pin To Taskbar</b>" <img src="/img/pinToTaskbar.png"></span>
        <span>then enjoy quick, easy access to recipes and news!</span>
        <button class="button is-info has-text-weight-bold" v-on:click="hidePinToTaskbar()">Done!</button>
      </div>
    </div>
    <div id="quicklinks">
      <a v-for="link in quicklinks" :key="link.key" :href="link.url" target="_blank" v-on:click="$impressions.externalClick(link.impression, link.url)">
        <img :src="link.image" :alt="link.name">
        <span>{{ link.name }}</span>
      </a>
    </div>
    <nav>
      <div id="branding">
        <img src="/img/logo.png" alt="Logo" v-on:click="$router.push('/')">
      </div>
      <div id="right">
        <form v-if="details" id="web-search" action="https://query.hmyrecipefinder.co/s" target="web_search" method="get" v-on:submit="fireWebQuery($event)">
          <input v-model="webQuery" type="text" name="query" placeholder="Search the web..." autocomplete="off" />
          <input type="hidden" name="uid" :value="details.uid">
          <input type="hidden" name="uc" :value="details.uc">
          <input type="hidden" name="source" :value="details.src">
          <input type="hidden" name="i_id" value="recipes_pwa">
          <input type="hidden" name="ap" :value="details.ad">
          <input type="hidden" name="cid" value="">
          <button type="submit"><i class="far fa-search"></i></button>
        </form>
      </div>
      <div id="tabs">
        <a v-if="weather" href="javascript:false" v-on:click="$impressions.externalClick(`tab_weather`, `https://weather.com/`)">
          <img :src="'https://openweathermap.org/img/wn/' + weather['weather'][0]['icon'] + '@2x.png'" alt="">
          {{ Math.round(weather['main']['temp']) }}&deg;F <b>{{ weather['name'] }}</b>
        </a>
        <router-link to="/">Recipes</router-link>
        <router-link to="/news">News</router-link>
        <a href="javascript:false" v-on:click="$impressions.externalClick(`tab_videos`, `https://go.totalvideos.tv/?uc=${details.uc}&ap=${details.ad}&source=${details.src}&uid=${details.uid}=&cid=recipes_pwa`)">Videos</a>
        <form id="recipes-search" v-on:submit="fireRecipeQuery($event)">
          <input type="text" name="query" v-model="recipeQuery" placeholder="Search for recipes..." autocomplete="off">
          <button type="submit"><i class="far fa-search"></i></button>
        </form>
      </div>
    </nav>
    <main><router-view /></main>
    <footer>
      <span>&copy; {{ new Date().getFullYear() }} My Recipe Finder</span>
      <a href="javascript:false" v-on:click="$impressions.externalClick(`legal_terms`, `https://legal.myrecipefinder.co/terms`)">Terms</a>
      <a href="javascript:false" v-on:click="$impressions.externalClick(`legal_privacy`, `https://legal.myrecipefinder.co/privacy`)">Privacy</a>
    </footer>
  </div>
</template>

<style scoped lang="sass">
  @import "styles/variables"

  #pin-to-taskbar
    display: block
    position: fixed
    top: 0
    right: 0
    bottom: 0
    left: 0
    background-color: rgba(0, 0, 0, 0.9)
    z-index: 10000

    .content
      position: absolute
      left: 50%
      transform: translateX(-50%)
      bottom: 40px
      text-align: left

      span
        display: block
        color: #fff
        vertical-align: middle
        font: 500 30px $font-sansSerif
        margin: 0 0 15px 0

        img
          display: inline-block
          vertical-align: middle
          padding: 5px
          background-color: rgba(255,255,255,0.2)
          border-radius: 6px
          height: 60px
          margin: 0 5px

      span:nth-of-type(2)
        img
          height: 100px
          padding: 0
          border: 1px solid #fff
          border-radius: 2px
          background-color: transparent

  #handholding
    display: block
    position: fixed
    top: 0
    right: 0
    bottom: 0
    left: 0
    background-color: rgba(0, 0, 0, 0.9)
    z-index: 100000

    #handholding-wrapper
      position: absolute
      top: 150px
      right: 210px

      i
        position: absolute
        right: 0
        top: 0
        display: block
        color: #3695ff
        font-size: 100px
        animation: arrowBounce 500ms alternate infinite linear

      span
        display: block
        margin-top: 100px
        text-align: center
        background-color: #fff
        font: 600 25px "Merriweather", "Times New Roman"
        padding: 20px 40px
        color: #444
        border-radius: 4px
</style>


<script>
  export default {
    async beforeCreate() {
      const bulmaCss = document.createElement('link');
      bulmaCss.href = 'https://cdn.jsdelivr.net/npm/bulma@0.9.0/css/bulma.min.css';
      bulmaCss.rel = 'stylesheet';
      document.head.appendChild(bulmaCss);

      this.$impressions.sendEvent('app_open').catch(error => console.error(error));

      window.OneSignal.push(() => {
        window.OneSignal.showNativePrompt();
        window.OneSignal.on('subscriptionChange', (isSubscribed) => {
          this.$impressions.sendEvent(isSubscribed ? 'app_push_accepted' : 'app_push_denied');
        });
      });

      let resizeTimeout = null;
      window.onresize = function(){
        resizeTimeout = setTimeout(() => {
          if (window.outerWidth < 950) window.resizeTo(950, window.outerHeight)
          if (window.outerHeight < 815) window.resizeTo(window.outerWidth, 815)
          clearTimeout(resizeTimeout);
        }, 150);
      };

      document.onvisibilitychange = () => document.visibilityState === 'unloaded' ?
          this.$impressions.sendEvent('app_closed') :
          this.$impressions.sendEvent('app_visibility_change', document.visibilityState);

      this.$recipes.GetRandom().then(() => this.recipesLoaded = true);
      this.$news.GetLatest().then(() => this.newsLoaded = true);

      if ((await this.$weather.checkPositionPerm()) === 'default') {
        this.handholdingText = 'Click "Allow" to get weather updates!';
        while ((await this.$weather.checkPositionPerm()) === 'default') await this.wait(100);
        this.handholdingText = undefined;
        this.$impressions.sendEvent((await this.$weather.checkPositionPerm()) === 'granted' ? 'app_geolocation_accepted' : 'app_geolocation_denied').catch(error => console.error(error));
      }
      this.weather = await this.$weather.Get();

      if (Notification.permission === 'default' && window.OneSignal.initialized) {
        this.handholdingText = 'Click "Allow" to get news and recipe updates!';
        while (Notification.permission === 'default') await this.wait(100);
        this.handholdingText = undefined;
      }

      if (localStorage.getItem('PinToTaskbarShown') !== 'true') {
        this.$impressions.sendEvent('app_pin_to_taskbar_shown')
        this.showPinToTaskbar = true;
      }
    },
    mounted() {
      document.querySelector('#web-search input[type=text]').focus()
    },
    methods: {
      wait(ms) {
        return new Promise(r => setTimeout(() => r(), ms))
      },
      fireRecipeQuery(evt) {
        evt.preventDefault();

        const query = document.querySelector('#recipes-search input[type=text]').value;
        if (this.recipeQuery.length < 1) return false;
        this.$impressions.sendEvent('app_recipes_query', query);
        this.$router.push('/search?query=' + this.recipeQuery);
        this.recipeQuery = '';
      },
      fireWebQuery(evt) {
        if (this.webQuery.length < 1) return evt.preventDefault();

        this.$impressions.sendEvent('app_web_query', this.webQuery);

        let height = window.outerHeight,
            width = window.outerWidth,
            top = window.screenY,
            windowWidth = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width,
            dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screen,
            left = ((windowWidth / 2) - (width / 2)) + dualScreenLeft + 8,
            searchWindow = window.open(`about:blank`, `web_search`, `left=${left + width},top=${top},height=${height},width=${width/2}`);

        searchWindow.moveTo(left + width, top);
        setTimeout(() => this.webQuery = '', 2000)
      },
      hidePinToTaskbar() {
        this.$impressions.sendEvent('app_pin_to_taskbar_hidden')
        this.showPinToTaskbar = false;
        localStorage.setItem('PinToTaskbarShown', 'true')
      }
    },
    data() {
      return {
        recipesLoaded: false,
        newsLoaded: false,
        showPinToTaskbar: false,
        details: this.$impressions.getDetails(),
        weather: undefined,
        handholdingText:undefined,
        recipeQuery: undefined,
        webQuery: undefined,
        quicklinks:[
          {
            name: 'Amazon',
            image: 'https://cdn.45tu1c0.com/account/74042/200/1521228087103.jpg',
            url: 'https://internal_tiles.ampxdirect.com?partner=internal_tiles&sub1=10058&sub2=recipes&sub3=74301&source=als_tiles',
            impression: 'quicklink_amazon'
          },
          {
            name: 'Total Recipes Network',
            image: 'https://dap2y8k6nefku.cloudfront.net/quicklinkicons/totalrecipesnetwork.png',
            url: `https://totalrecipesnetwork.com/?ap=appfocus1&source=pwa&utm_content=recipes_pwa&utm_term=quicklink`,
            impression: 'quicklink_recipes'
          },
          {
            name: 'Recipe Videos',
            image: 'https://dap2y8k6nefku.cloudfront.net/quicklinkicons/totalvideos_recipes.png',
            url: `https://go.totalvideos.tv/category/food/recipes/?ap=appfocus1&source=pwa&utm_content=recipes_pwa&utm_term=quicklink`,
            impression: 'quicklink_videos'
          },
          {
            name: 'Food Network',
            image: 'https://dap2y8k6nefku.cloudfront.net/quicklinkicons/foodnetwork.png',
            url: 'https://www.foodnetwork.com/',
            impression: 'quicklink_foodnetwork'
          },
          {
            name: 'Home Depot',
            image: 'https://cdn.45tu1c0.com/account/74437/200/1521228087115.jpg',
            url: 'https://internal_tiles.ampxdirect.com?partner=internal_tiles&sub1=10058&sub2=recipes&sub3=74437&source=als_tiles',
            impression: 'quicklink_homedepot'
          },
          {
            name: 'Email Tips',
            image: 'https://dap2y8k6nefku.cloudfront.net/quicklinkicons/myemailsimplified.png',
            url: `https://go.myemailcentral.com/?ap=appfocus1&source=pwa&utm_content=recipes_pwa&utm_term=quicklink`,
            impression: 'quicklink_email'
          },
          {
            name: 'News',
            image: 'https://dap2y8k6nefku.cloudfront.net/quicklinkicons/thenewscorner.png',
            url: `https://go.thenewscorner.org/?ap=appfocus1&source=pwa&utm_content=recipes_pwa&utm_term=quicklink`,
            impression: 'quicklink_news'
          },
          {
            name: 'Weather',
            image: 'https://dap2y8k6nefku.cloudfront.net/quicklinkicons/nationalweatheragency.png',
            url: `https://go.nationalweatheragency.org/?ap=appfocus1&source=pwa&utm_content=recipes_pwa&utm_term=quicklink`,
            impression: 'quicklink_weather'
          },
        ]
      }
    }
  }
</script>